import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadDocument } from '@steelbuy/data-access';
import { DocumentModel, DocumentType } from '@steelbuy/ts-shared';
import { exceedsMaxFileSize, FILE_SIZE_LIMIT_IN_MEGABYTES, isValidFileType } from '@steelbuy/ui-domain';
import { FileUploadStatus } from '@steelbuy/ui-primitive';

const VALID_CERTIFICATE_FILES = ['.pdf', '.png', '.jpg'];
const VALID_PICTURE_FILES = ['.png', '.jpg'];

export const useFileInputStatus = ({
    type,
    onSuccess,
    onError,
    bulkId,
}: {
    type: DocumentType;
    onSuccess?: (data: DocumentModel) => void;
    onError?: () => void;
    bulkId?: string;
}) => {
    const { t } = useTranslation(['uiDomain']);
    const [fileStatus, setFileStatus] = useState({});

    const updateFileStatus = (filename: string, status: FileUploadStatus) => {
        setFileStatus({ [filename]: status });
    };

    const uploadDocument = useUploadDocument({
        onSuccess: (data) => {
            updateFileStatus(data.fileName, FileUploadStatus.Success);
            onSuccess?.(data);
        },
        onError: (_, { file: uploadedFile }) => {
            updateFileStatus(uploadedFile.name, FileUploadStatus.Error);
            onError?.();
        },
    });

    const handleFileSelection = (files?: File[]) => {
        if (files) {
            files.forEach((uploadedFile) => {
                if (exceedsMaxFileSize(uploadedFile.size) || !isValidFileType(type, uploadedFile.name)) {
                    updateFileStatus(uploadedFile.name, FileUploadStatus.Error);
                } else {
                    updateFileStatus(uploadedFile.name, FileUploadStatus.Uploading);
                    uploadDocument.mutateAsync({ file: uploadedFile, type, bulkId });
                }
            });
        }
    };

    const onFileUpload = (files?: File[]) => {
        const uploadedFile = files?.[0];
        if (uploadedFile) {
            const nameAndType = uploadedFile.name.split('.');
            if (nameAndType.length < 2) {
                updateFileStatus(files[0].name, FileUploadStatus.Error);
                return false;
            }

            const extension = nameAndType.pop()?.toLowerCase();
            if (!VALID_CERTIFICATE_FILES.includes(`.${extension}`)) {
                updateFileStatus(uploadedFile.name, FileUploadStatus.Error);
                return false;
            }
            handleFileSelection(files);
            return true;
        }
        return false;
    };

    const getInvalidMessage = useCallback((file: File) => {
        if (!isValidFileType(type, file.name)) {
            return t('uiDomain:validation.unsupporttedFileType');
        }
        if (exceedsMaxFileSize(file.size)) {
            return t('uiDomain:validation.fileItemSizeError', {
                sizeLimitInMegabytes: FILE_SIZE_LIMIT_IN_MEGABYTES.toString(),
            });
        }
        return undefined;
    }, []);

    const reset = useCallback(() => {
        setFileStatus({});
    }, [setFileStatus]);

    return {
        validFileTypes: type === DocumentType.PICTURE ? VALID_PICTURE_FILES : VALID_CERTIFICATE_FILES,
        fileStatus,
        setFileStatus,
        reset,
        onFileUpload,
        getInvalidMessage,
    };
};
